/* eslint-disable @typescript-eslint/no-explicit-any */
import { CategoriesResponse } from "@app/services/category/types";
import dayjs from "dayjs";
import { useMemo } from "react";
import { warningNotification } from "../components/notifications";
import { UpdatedBy } from "../../services/supportParams/types";

/**
 * Calculate the pages for a table depending on the row total.
 * @param {number} total
 * @returns {number} Calculated pages for the total
 */
export const calculatePagination = (total: number) => {
  return Math.ceil(Math.ceil(total) / 10);
};

export const statusListSelect = [
  {
    label: "Habilitado",
    value: "enabled"
  },
  { label: "Deshabilitado", value: "disabled" }
];

/**
 * Transform a number into a currency value.
 * @param {number} number
 * @returns {string} Formatted number to currency
 */
export function formatNumberToCop(number: number): string {
  const str = number.toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  });
  return str;
}

export function chunkPagination<T>(array: T[], size: number): T[][] {
  if (!array.length) {
    return [];
  }
  const head = array.slice(0, size);
  const tail = array.slice(size);
  return [head, ...chunkPagination(tail, size)];
}

export const containsNumbers = (text: string) => /\d/.test(text);

export const validateEmail = (value: string) => {
  const valid = /^\w+([.-_+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/;
  const response = valid.test(value) ? true : false;
  return response;
};

export const fileToBase64 = (file: File | null): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(",").at(-1) || "");
    reader.onerror = (error) => reject(error);
  });
};

export const formattedDataSelect = <T extends Record<string, any>, K extends keyof T>(
  data: T[],
  label: K,
  value: K,
  extra?: K[],
  defaultOption = true
) => {
  const toMap = data.map((item) => {
    const base = {
      value: String(item[value]),
      label: String(item[label])
    };

    const extraFields = extra?.length
      ? extra.reduce(
          (acc, key) => {
            acc[key] = item[key];
            return acc;
          },
          {} as Pick<T, K>
        )
      : {};

    return { ...base, ...extraFields };
  });

  if (defaultOption) {
    return [{ label: "Seleccionar...", value: "" }, ...toMap];
  }
  return toMap;
};

export const UseFormattedDataSelect = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[],
  label: string,
  value: string | number,
  defaultOption = true
) => {
  return useMemo(() => {
    const toMap = data.map((item) => ({
      value: String(item[value]),
      label: String(item[label])
    }));

    if (defaultOption) {
      return [{ label: "Seleccionar...", value: "" }, ...toMap];
    }
    return [...toMap];
  }, [data, label, value, defaultOption]); // Solo recalculará si estos valores cambian
};

export const today = () => new Date().toISOString().split("T")[0];

export const formatDate = (date: string) => dayjs(date).format("DD/MM/YYYY");

export const formatUpdatedInfo = (
  name = "",
  date: string | number | Date = "",
  dateformat = "DD/MM/YYYY HH:mm a"
) => {
  const validDate = dayjs(date).isValid();
  const cleanData = [name, validDate ? dayjs(date).format(dateformat) : ""].filter(
    (value) => value !== ""
  );

  return cleanData.join(" - ");
};

export const formatLastUpdate = (
  updatedAt?: string | null,
  updatedBy?: UpdatedBy | null
): string => {
  if (!updatedAt) {
    return "-";
  }
  const date = dayjs(updatedAt).format("DD/MM/YYYY hh:mm A");
  if (updatedBy?.firstName && updatedBy?.firstSurname) {
    return `${updatedBy.firstName} ${updatedBy.firstSurname} - ${date}`;
  }
  return date;
};

export const formatTimeToAmPm = (time: string): string => {
  if (time) {
    return dayjs("1/1/1 " + time).format("hh:mm a");
  }
  return "-";
};

export const formatUpdatedAt = (updatedAt?: string | null): string => {
  if (!updatedAt) {
    return "";
  }
  return dayjs(updatedAt).format("DD/MM/YYYY hh:mm A");
};

export const dateValidation = (endDate: string | undefined, startDate: string | undefined) => {
  if (endDate && startDate && endDate < startDate) {
    warningNotification("El campo 'fecha hasta', tiene que ser mayor al campo 'fecha desde' ");
    return false;
  }
  return true;
};

export const mergeArrays = <T extends { id: string | number; medicineId: number | string }>(
  array1: T[],
  array2: T[],
  preferredArray: "array1" | "array2"
): T[] => {
  const map = new Map<string | number, T>();
  const cleanArr = array1.filter((x) => x.medicineId !== array2.at(0)?.medicineId);
  const prioritizedArrays =
    preferredArray === "array1" ? [...array2, ...cleanArr] : [...cleanArr, ...array2];

  prioritizedArrays.forEach((item) => {
    if (!map.has(item.id)) {
      map.set(item.id, item);
    }
  });

  return Array.from(map.values());
};

export const moduleInfo = (urlToSearch: string, allCategory: CategoriesResponse) => {
  const allModules = allCategory.new_category?.flatMap((category) => category.data) ?? [];
  const normalizeUrl = (url: string) => url.replace(/\/$/, "");
  const module = allModules.find((item) => normalizeUrl(item.url) === normalizeUrl(urlToSearch));
  return module;
};

export function isAlphanumeric(value: string): boolean {
  const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñ0-9\s]+$/;
  return regex.test(value);
}

export const clearEmptyKeys = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const isEmpty =
      value === null || value === undefined || (typeof value === "string" && value.trim() === "");

    if (!isEmpty) {
      acc[key as keyof T] = value;
    }

    return acc;
  }, {} as Partial<T>);
};

export const normalizeText = (text: string): string => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
};

export const capitalizeFirst = (text: string): string => {
  if (!text) {
    return "";
  }

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};
